import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { useHistory } from 'react-router-dom';
import { getRoundReportStorageItem, updateRoundReportStorageItem } from 'actions/admin/roundReportStorageItems';
import { createStorageNote } from 'actions/admin/storages';
import { fetchPredefinedTexts } from 'actions/predefinedTexts';
import { createMessageForBooking } from 'actions/admin/conversations';
import { handleResponse } from 'actions/actionHelpers';
import { convertToNumber } from 'helpers/StringHelper';
import routes from 'routes';
import conversationCategories from 'enums/conversationCategories';
import conversationStatuses from 'enums/conversationStatuses';

import Loader from 'common/Loader';
import AdminPage from '../../presentational/AdminPage';
import AdminContentWrapper from '../../presentational/AdminContentWrapper';
import AdminActionWrapper from '../../presentational/AdminActionWrapper';
import Button from '@material-ui/core/Button';
import EditNoteDialog from 'components/EditNoteDialog';
import EditRoundReportStorageItemForm from './EditRoundReportStorageItemForm';
import SendMessageDialog from 'admin/containers/sendMessage/SendMessageDialog';

const RoundReportStorageItem = () => {
    const dispatch = useDispatch();
    const history = useHistory();
    const params = useParams();
    const { user } = useSelector(state => state.authentication.context);

    const [roundReportStorageItem, setRoundReportStorageItem] = useState(undefined);
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [noteDialogOpen, setNoteDialogOpen] = useState(false);
    const [sendMessageDialogOpen, setSendMessageDialogOpen] = useState(false);
    const [selectedBooking, setSelectedBooking] = useState(undefined);
    const [predefinedTexts, setPredefinedTexts] = useState(undefined);
    const { roundReportStorageItemId } = params;

    useEffect(() => {
        loadRoundReportStorageItem();
    }, [roundReportStorageItemId]);

    useEffect(() => {
        dispatch(fetchPredefinedTexts('ownerActorConversations'))
            .then(handleResponse(
                response => {
                    setPredefinedTexts(response.payload);
                }
            ));
    }, []);

    const loadRoundReportStorageItem = () => {
        setIsLoading(true);
        dispatch(getRoundReportStorageItem(roundReportStorageItemId))
            .then(handleResponse(
                response => {
                    setRoundReportStorageItem(response.payload);
                    setIsLoading(false);
                },
                () => setIsLoading(false)
            ));
    };

    const handleSubmit = formValues => {
        setIsSubmitting(true);
        dispatch(updateRoundReportStorageItem(roundReportStorageItemId, formValues))
            .then(handleResponse(
                () => {
                    history.push(routes.admin.roundReportStorageItems);
                    setIsSubmitting(false);
                    return { snackbar: { message: 'Round report storage item was successfully saved.' } };
                },
                () => setIsSubmitting(false)
            ));
    };

    const handleCancel = () => {
        history.push(routes.admin.roundReportStorageItems);
    };

    const handleAddNoteClick = () => {
        setNoteDialogOpen(true);
    };

    const handleNoteDialogSubmit = (formValues, form) => {
        setIsSubmitting(true);
        setNoteDialogOpen(false);
        const request = {
            text: formValues.text
        };
        dispatch(createStorageNote(roundReportStorageItem.storageId, request))
            .then(handleResponse(
                response => {
                    const newRoundReportStorageItem = { ...roundReportStorageItem };
                    newRoundReportStorageItem.notes.push(response.payload);
                    setRoundReportStorageItem(newRoundReportStorageItem);
                    form.reset();
                    setIsSubmitting(false);
                    return { snackbar: { message: 'Note was successfully saved.' } };
                },
                () => {
                    setIsSubmitting(false);
                }
            ));
    };

    const handleNoteDialogCancel = () => {
        setNoteDialogOpen(false);
    };

    const handleSendEmailButtonClick = booking => {
        setSelectedBooking({
            id: booking.id,
            tenantActor: {
                id: booking.tenantActorId,
                name: booking.tenantName
            },
            ownerActor: {
                id: booking.ownerActorId,
                name: booking.ownerName
            },
        });
        setSendMessageDialogOpen(true);
    };

    const handleSendMessageDialogSubmit = formValues => {
        setSendMessageDialogOpen(false);
        const createMessageRequest = {
            senderUserId: user.id,
            senderActorId: convertToNumber(formValues.senderActorId),
            sendAsAdmin: formValues.sendAsAdmin,
            category: conversationCategories.general.key,
            body: formValues.body,
            status: conversationStatuses.waitingForTenant.key
        };
        setIsSubmitting(true);
        dispatch(createMessageForBooking(selectedBooking.id, createMessageRequest))
            .then(handleResponse(
                () => {
                    setIsSubmitting(false);
                    return { snackbar: { message: 'Message was successfully sent.' } };
                },
                () => setIsSubmitting(false)
            ));
    };

    const handleSendMessageDialogCancel = () => {
        setSendMessageDialogOpen(false);
    };

    const title = 'Round report storage item: ' + (roundReportStorageItem ? `${roundReportStorageItem.storageSiteTitle}, ${roundReportStorageItem.storageTitle}` : 'Loading...');

    return (
        <AdminPage title={title}>
            {
                isLoading && <Loader />
            }
            {
                !isLoading && roundReportStorageItem && (
                    <AdminContentWrapper type="detail">

                        <EditRoundReportStorageItemForm
                            roundReportStorageItem={roundReportStorageItem}
                            onSubmit={handleSubmit}
                            onCancel={handleCancel}
                            isSubmitting={isSubmitting}
                            onSendEmailButtonClick={handleSendEmailButtonClick}
                        />

                        <AdminActionWrapper>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleAddNoteClick}
                            >
                                Add note
                            </Button>
                        </AdminActionWrapper>

                    </AdminContentWrapper>
                )
            }
            <EditNoteDialog
                open={noteDialogOpen}
                onSubmit={handleNoteDialogSubmit}
                onCancel={handleNoteDialogCancel}
            />
            {
                selectedBooking &&
                (
                    <SendMessageDialog
                        open={sendMessageDialogOpen}
                        onSubmit={handleSendMessageDialogSubmit}
                        onCancel={handleSendMessageDialogCancel}
                        tenantActor={selectedBooking.tenantActor}
                        ownerActor={selectedBooking.ownerActor}
                        predefinedTexts={predefinedTexts}
                    />
                )
            }
        </AdminPage>
    );
};

export default RoundReportStorageItem;
